import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useRef, useState } from "react";
import Subheader from "../components/Subheader";
import Api from "../openapi";
import { MealSelectionsByTypeResponse } from "../openapi/client";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import html2pdf from "html2pdf.js";
import { saveAs } from "file-saver";
import { ArrowRightFillIcon, CrossIcon, DownloadIcon, DraftIcon } from "../assets/icons";
import { checkEmpty, defaultFormat } from "../utils";
import { MainSpinner } from "../components";
import { LoadingButton } from "@mui/lab";
import { Transition } from "../hooks/useConfirm";
import useDietitians from "../hooks/useDietitians";
import useAffiliates from "../hooks/useAffiliates";
import useUsers from "../hooks/useUsers";
import { useDispatch } from "react-redux";
import { setNotification } from "../store/notificationSlice";
import { useMeals } from "../hooks";
import Can from "../components/Can";

export default function KitchenReport() {
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [packages, setPackages] = useState<MealSelectionsByTypeResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [deliveryPackageReportDownloadProcess, setDeliveryPackageReportDownloadProcess] = useState(false);
  const [kitchenReportDownloadProcess, setKitchenReportDownloadProcess] = useState(false);
  const [mealBoxStickersDownloadProcess, setMealBoxStickersDownloadProcess] = useState(false);
  const [mealPackageStickersDownloadProcess, setMealPackageStickersDownloadProcess] = useState(false);
  const [dailyMealsReportDownloadProcess, setDailyMealsReportDownloadProcess] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [activeDownload, setActiveDownload] = useState<string>();
  const reference = useRef<HTMLElement>();

  const dietitianList = useDietitians();
  const affiliateList = useAffiliates();
  const userList = useUsers();
  const meals = useMeals();
  const [selectedMeal, setSelectedMeal] = useState<string>(" ");

  const defaultValue = {
    dietitianId: " ",
    userId: " ",
    affiliateId: " ",
  };

  const [selection, setSelection] = useState(defaultValue);

  const pdfHandler = async () => {
    const opt = {
      margin: [40, 0, 40, 0],
      filename: `kitchenReport-${selectedDate.format(defaultFormat)}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        // foreignObjectRendering: true,
        // scrollX: 0,
        // scrollY: 0,
        // backgroundColor: null,
        // allowTaint: true,
        width: 1240,
      },
      jsPDF: {
        unit: "px",
        format: [1240, 1754],
        hotfixes: ["px_scaling"],
      },
    };
    setKitchenReportDownloadProcess(true);

    setTimeout(() => {
      html2pdf()
        .from(reference.current)
        .set(opt)
        .toPdf()
        .get("pdf")
        .then(function (pdf: any) {
          var totalPages = pdf.internal.getNumberOfPages();
          for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            pdf.setFontSize(14);
            pdf.setTextColor(100);
            pdf.text(
              "Page " + i + " of " + totalPages,
              pdf.internal.pageSize.getWidth() / 2.3,
              pdf.internal.pageSize.getHeight() - 30
            );
          }
          setKitchenReportDownloadProcess(false);
        })
        .save();
    });
  };

  const refactorSelectionRequest = (selection: any) => {
    return [
      selection.userId?.toString().trim() || undefined,
      selection.dietitianId?.toString().trim() || undefined,
      selection.affiliateId?.toString().trim() || undefined,
    ];
  };

  const deliveryPackageReportDownload = async () => {
    setDeliveryPackageReportDownloadProcess(true);
    const filters = refactorSelectionRequest(selection);
    try {
      const res = await Api.UserMenu.downloadDeliveryPackagesReport(
        selectedDate.format(defaultFormat),
        filters[0],
        filters[1],
        filters[2],
        {
          responseType: "blob",
        }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      saveAs(blob, `delivery-package-report-${selectedDate.format(defaultFormat)}.pdf`);
    } catch (error) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: "No data found for the given filters",
        })
      );
    }
    setDeliveryPackageReportDownloadProcess(false);
  };

  const mealBoxStickersDownload = async () => {
    setMealBoxStickersDownloadProcess(true);
    const filters = refactorSelectionRequest(selection);
    try {
      const res = await Api.Delivery.downloadMealBoxStickers(
        selectedDate.format(defaultFormat),
        filters[0],
        filters[1],
        filters[2],
        {
          responseType: "blob",
        }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      saveAs(blob, `meal-box-stickers-${selectedDate.format(defaultFormat)}.pdf`);
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: "No data found for the given filters",
        })
      );
    }
    setMealBoxStickersDownloadProcess(false);
  };

  const mealPackageStickersDownload = async () => {
    setMealPackageStickersDownloadProcess(true);
    const filters = refactorSelectionRequest(selection);
    try {
      const res = await Api.Delivery.downloadMealPackageStickers(
        selectedDate.format(defaultFormat),
        filters[0],
        filters[1],
        filters[2],
        {
          responseType: "blob",
        }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      saveAs(blob, `meal-package-stickers-${selectedDate.format(defaultFormat)}.pdf`);
    } catch (error) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: "No data found for the given filters",
        })
      );
    }
    setMealPackageStickersDownloadProcess(false);
  };

  const dailyMealsReportDownload = async () => {
    setDailyMealsReportDownloadProcess(true);
    try {
      const res = await Api.Delivery.downloadMealDailyUsers(selectedDate.format(defaultFormat), selectedMeal, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      saveAs(blob, `daily-meals-report-${selectedDate.format(defaultFormat)}.pdf`);
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: "No data for the given date and meal",
        })
      );
    }
    setDailyMealsReportDownloadProcess(false);
  };

  const handlePanelClose = (...args: any) => {
    setDialogOpen(false);
    setSelection(defaultValue);
    setSelectedMeal(" ");
  };

  const handleFieldChange = (field: string, value: any) => {
    setSelection((state: any) => ({ ...state, [field]: value }));
  };

  const download = () => {
    switch (activeDownload) {
      case "mealPackageStickersDownload":
        mealPackageStickersDownload();
        break;
      case "mealBoxStickersDownload":
        mealBoxStickersDownload();
        break;
      case "deliveryPackageReportDownload":
        deliveryPackageReportDownload();
        break;
      case "dailyMealsReportDownload":
        dailyMealsReportDownload();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const getPackages = async () => {
      setLoading(true);
      try {
        const res = await Api.UserMenu.getSelectionsByPortion(selectedDate.format(defaultFormat));
        setPackages(res.data);
      } catch (error) {}
      setLoading(false);
    };
    getPackages();
  }, [selectedDate]);

  const dialogTitle = `${
    activeDownload === "dailyMealsReportDownload" ? "Daily Meal List by Members" : "Apply Filters"
  }`;

  return (
    <>
      <Box className="delivery-report">
        <Box className="delivery-report-upper">
          <Box className="delivery-report-upper-left">
            <Subheader page="Kitchen Report" />
          </Box>
          <Box className="delivery-report-upper-right">
            <Box className="delivery-report-selection-wrapper">
              <Box sx={{ width: 250 }}>
                <FormControl fullWidth>
                  <DatePicker
                    className="delivery-report-date-selector"
                    sx={{ "& .MuiOutlinedInput-root": { height: "44px" }, "& fieldset": { border: "none" } }}
                    value={selectedDate}
                    onChange={(date) => setSelectedDate(moment(date))}
                    format="DD/MM/YYYY"
                  />
                </FormControl>
              </Box>
            </Box>

            <Can permission="KITCHEN_REPORT_CREATE">
              <LoadingButton
                loading={deliveryPackageReportDownloadProcess}
                onClick={() => {
                  setDialogOpen(true);
                  setActiveDownload("deliveryPackageReportDownload");
                }}
                endIcon={<DownloadIcon />}
                sx={{
                  "&, &:hover": { boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)" },
                  background: "#fff",
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "normal",
                  display: "flex",
                  padding: "12px",
                  "&": { color: "text.dark2" },
                  "&:hover": { background: "#fff" },
                }}
              >
                Package Report
              </LoadingButton>
            </Can>
            <Can permission="KITCHEN_REPORT_CREATE">
              <LoadingButton
                loading={dailyMealsReportDownloadProcess}
                onClick={() => {
                  setDialogOpen(true);
                  setActiveDownload("dailyMealsReportDownload");
                }}
                endIcon={<DownloadIcon />}
                sx={{
                  "&, &:hover": { boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)" },
                  background: "#fff",
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "normal",
                  display: "flex",
                  padding: "12px",
                  "&": { color: "text.dark2" },
                  "&:hover": { background: "#fff" },
                }}
              >
                Daily Meals
              </LoadingButton>
            </Can>
            <Can permission="KITCHEN_REPORT_CREATE">
              <LoadingButton
                loading={mealBoxStickersDownloadProcess}
                onClick={() => {
                  setDialogOpen(true);
                  setActiveDownload("mealBoxStickersDownload");
                }}
                endIcon={<DownloadIcon />}
                sx={{
                  "&, &:hover": { boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)" },
                  background: "#fff",
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "normal",
                  display: "flex",
                  padding: "12px",
                  "&": { color: "text.dark2" },
                  "&:hover": { background: "#fff" },
                }}
              >
                Box Stickers
              </LoadingButton>
            </Can>
            <Can permission="KITCHEN_REPORT_CREATE">
              <LoadingButton
                loading={mealPackageStickersDownloadProcess}
                onClick={() => {
                  setDialogOpen(true);
                  setActiveDownload("mealPackageStickersDownload");
                }}
                endIcon={<DownloadIcon />}
                sx={{
                  "&, &:hover": { boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)" },
                  background: "#fff",
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "normal",
                  display: "flex",
                  padding: "12px",
                  "&": { color: "text.dark2" },
                  "&:hover": { background: "#fff" },
                }}
              >
                Package Stickers
              </LoadingButton>
            </Can>
            <Can permission="KITCHEN_REPORT_CREATE">
              <LoadingButton
                loading={kitchenReportDownloadProcess}
                onClick={pdfHandler}
                sx={{
                  "&, &:hover": { boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)" },
                  background: "#fff",
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "normal",
                  display: "flex",
                  padding: "12px",
                  "&, & svg": { color: "text.dark2" },
                  "&:hover": { background: "#fff" },
                }}
                endIcon={<DownloadIcon />}
              >
                Download
              </LoadingButton>
            </Can>
          </Box>
        </Box>
        {loading ? (
          <MainSpinner sx={{ backgroundColor: "transparent", paddingTop: "150px" }} />
        ) : (
          <Box
            sx={{
              padding: "24px",
              background: "#fff",
              borderRadius: "8px",
              boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
              display: "flex",
              flexDirection: "column",
              rowGap: "24px",
            }}
          >
            <Box className="tab-container">
              <Box className={`tab ${activeTab === "all" ? "active" : ""}`} onClick={() => setActiveTab("all")}>
                <Typography className="meal-type">All</Typography>
                <Typography className="meal-number">
                  {packages.reduce((acc, v) => Number(v.totalCount) + acc, 0)} Items
                </Typography>
              </Box>

              {packages.map((mealType) => {
                return (
                  <Box
                    onClick={() => setActiveTab(String(mealType.typeName))}
                    key={mealType.typeName}
                    className={`tab ${activeTab === mealType.typeName ? "active" : ""}`}
                  >
                    <Typography className="meal-type">{mealType.typeName}</Typography>
                    <Typography className="meal-number">{mealType.totalCount} Items</Typography>
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", rowGap: "24px" }}>
              {packages.map((mealType) => {
                return (
                  <Box
                    sx={{
                      display: `${activeTab === mealType.typeName || activeTab === "all" ? "flex" : "none"}`,
                      flexDirection: "column",
                      rowGap: "24px",
                    }}
                    key={mealType.typeName}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography sx={{ color: "text.blue", fontSize: "16px", fontWeight: 700, lineHeight: "normal" }}>
                        {mealType.typeName}
                      </Typography>
                      <Typography sx={{ color: "text.blue", fontSize: "14px", fontWeight: 500, lineHeight: "normal" }}>
                        {mealType.totalCount} Items / {mealType.totalPortion} Portions
                      </Typography>
                    </Box>
                    {mealType.selections?.map((selection) => {
                      return (
                        <Box sx={{ display: "flex", flexDirection: "column", rowGap: "4px" }} key={selection.mealName}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography sx={{ color: "text.dark", fontSize: "14px", fontWeight: 600 }}>
                              {selection.mealName}
                            </Typography>
                            <Typography sx={{ color: "text.dark", fontSize: "14px", fontWeight: 600 }}>
                              {selection.totalCount} Items / {selection.totalPortion} {selection.unit?.toLowerCase()}
                            </Typography>
                          </Box>

                          <TableContainer component={Paper} sx={{ borderRadius: 0, boxShadow: "none" }}>
                            <Table
                              sx={{
                                minWidth: 650,
                                pageBreakInside: "avoid",
                                "& td": { padding: "8px 12px" },
                              }}
                            >
                              <TableBody>
                                {selection.affiliates?.map((affiliate) => {
                                  return (
                                    <>
                                      <TableRow sx={{ background: "#F4F5F6" }} key={affiliate.affiliateName}>
                                        <TableCell
                                          colSpan={4}
                                          sx={{ border: "none", borderLeft: "1px solid #F4F5F6", fontWeight: 600 }}
                                        >
                                          {affiliate.affiliateName}
                                        </TableCell>
                                        <TableCell sx={{ border: "none", textAlign: "right" }}>
                                          {affiliate.totalCount} Items / {affiliate.totalPortion}{" "}
                                          {selection.unit?.toLowerCase()}
                                        </TableCell>
                                      </TableRow>

                                      {affiliate.variants?.map((variant, key) => {
                                        return variant.dietitianNotes?.map((row, key) => {
                                          return (
                                            <TableRow key={key}>
                                              <TableCell width={100} sx={{ border: "1px solid #F4F5F6" }}>
                                                <Typography
                                                  sx={{
                                                    color: "text.dark",
                                                    fontSize: "14px",
                                                    fontWeight: 400,
                                                    lineHeight: "normal",
                                                  }}
                                                >
                                                  {row.count} {row.count === 1 ? `Item` : `Items`}
                                                </Typography>
                                              </TableCell>
                                              <TableCell width={120} sx={{ border: "1px solid #F4F5F6" }}>
                                                <Typography
                                                  sx={{
                                                    color: "text.dark",
                                                    fontSize: "14px",
                                                    fontWeight: 400,
                                                    lineHeight: "normal",
                                                  }}
                                                >
                                                  {variant.portion} {selection.unit?.toLowerCase()}
                                                </Typography>
                                              </TableCell>
                                              <TableCell colSpan={3} sx={{ border: "1px solid #F4F5F6" }}>
                                                <Box sx={{ display: "flex", columnGap: "10px", alignItems: "center" }}>
                                                  <DraftIcon color="#575C66" />
                                                  <Typography
                                                    sx={{
                                                      color: "text.dark2",
                                                      fontSize: "14px",
                                                      fontWeight: 400,
                                                      lineHeight: "normal",
                                                    }}
                                                  >
                                                    {row.dietitianNote}
                                                  </Typography>
                                                </Box>
                                              </TableCell>
                                            </TableRow>
                                          );
                                        });
                                      })}
                                    </>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
      <Box position={"absolute"} left="-1111111px" sx={{ width: "1240px" }}>
        <Box ref={reference} className="print" sx={{ width: "100%", background: "#fff" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: "24px",
              padding: "36px 64px",
              borderBottom: "2px solid #37BD6B",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <img src="../../logo.png" />
            </Box>
            <Box
              sx={{
                display: "flex",
                columnGap: "16px",
              }}
            >
              <Typography sx={{ color: "#000", fontSize: "24px", fontWeight: 800, lineHeight: "normal" }}>
                {selectedDate.format("D MMM YYYY")}
              </Typography>
              <Box sx={{ width: 0, height: "29px", borderRight: "3px solid #000" }}></Box>
              <Typography sx={{ color: "#000", fontSize: "24px", fontWeight: 500, lineHeight: "normal" }}>
                Total Items: <b>{packages.reduce((acc, v) => Number(v.totalCount) + acc, 0)}</b>
              </Typography>
            </Box>
          </Box>

          {packages?.map((section, key) => {
            return (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "48px 64px",
                  "&:not(:last-of-type)": { borderBottom: "2px dashed #575C66" },
                  rowGap: "24px",
                  pageBreakInside: "avoid",
                  pageBreakAfter: "auto",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "#000", fontSize: "26px", fontWeight: 800, lineHeight: "normal" }}>
                    {section.typeName}
                  </Typography>
                  <Typography sx={{ color: "#000", fontSize: "26px", fontWeight: 700, lineHeight: "normal" }}>
                    {section.totalCount} Items / {section.totalPortion} Portions
                  </Typography>
                </Box>
                {section.selections?.map((selection, key) => {
                  return (
                    <Box key={key} sx={{ display: "flex", flexDirection: "column", rowGap: "4px" }}>
                      <TableContainer component={Paper} sx={{ borderRadius: 0, boxShadow: "none" }}>
                        <Table
                          sx={{
                            minWidth: 650,
                            pageBreakInside: "avoid",
                            "& tr": { pageBreakInside: "avoid", pageBreakAfter: "auto" },
                            "& td, & th": { padding: "6px 12px" },
                          }}
                        >
                          <TableHead sx={{}}>
                            <TableRow
                              sx={{
                                "& th, & td": { border: "none", fontSize: "20px", fontWeight: 700 },
                                "& th:last-of-type": { paddingRight: 0 },
                              }}
                            >
                              <TableCell width={30}>
                                <ArrowRightFillIcon color="#37BD6B" />
                              </TableCell>
                              <TableCell align="left" width={70}>
                                <Typography sx={{ color: "#000", fontSize: "22px", fontWeight: 800 }}>
                                  {selection.totalCount}
                                </Typography>
                              </TableCell>
                              <TableCell width={130}>
                                <Typography sx={{ color: "#000", fontSize: "22px", fontWeight: 800 }}>
                                  Dishes
                                </Typography>
                              </TableCell>
                              <TableCell colSpan={2}>
                                <Typography
                                  sx={{
                                    color: "#000",
                                    fontSize: "22px",
                                    fontWeight: 800,
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {selection.mealName}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                {selection.totalPortion} {selection.unit?.toLowerCase()}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selection.affiliates?.map((affiliate) => {
                              return (
                                <Fragment key={affiliate.affiliateName}>
                                  <TableRow>
                                    <TableCell sx={{ border: "none" }}>&nbsp;</TableCell>
                                    <TableCell colSpan={4} sx={{ border: "none", fontSize: "16px", fontWeight: 700 }}>
                                      {affiliate.affiliateName}
                                    </TableCell>
                                    <TableCell sx={{ border: "none", textAlign: "right" }}>
                                      {affiliate.totalCount} Items / {affiliate.totalPortion}{" "}
                                      {selection.unit?.toLowerCase()}
                                    </TableCell>
                                  </TableRow>

                                  {affiliate.variants?.map((variant, key) => {
                                    return variant.dietitianNotes?.map((row, key) => (
                                      <TableRow
                                        key={key}
                                        sx={{
                                          "& th, & td": { border: "none", fontSize: "20px", fontWeight: 700 },
                                        }}
                                      >
                                        <TableCell>&nbsp;</TableCell>
                                        <TableCell sx={{ border: "1px solid #F4F5F6" }}>
                                          <Typography
                                            sx={{
                                              color: "#000",
                                              fontSize: "18px",
                                              fontWeight: 600,
                                              lineHeight: "normal",
                                            }}
                                          >
                                            {row.count}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography
                                            sx={{
                                              color: "#000",
                                              fontSize: "18px",
                                              fontWeight: 600,
                                              lineHeight: "normal",
                                            }}
                                          >
                                            {variant.portion} {selection.unit?.toLowerCase()}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Box sx={{ display: "flex", columnGap: "10px", alignItems: "center" }}>
                                            <Typography
                                              sx={{
                                                color: "#000",
                                                fontSize: "18px",
                                                fontWeight: 600,
                                                lineHeight: "normal",
                                              }}
                                            >
                                              {row.dietitianNote}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    ));
                                  })}
                                </Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>
      <Fragment>
        <Dialog
          open={dialogOpen}
          TransitionComponent={Transition}
          onClose={handlePanelClose}
          aria-describedby="alert-dialog-slide-description"
          sx={{
            "& .MuiPaper-root": {
              borderRadius: "16px",
              boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.15)",
              padding: "24px",
              width: "564px",
              maxWidth: "564px",
            },
            "& .MuiDialogTitle-root": {
              padding: "0 16px 16px 0",
            },
            "& .MuiDialogContent-root,& .MuiDialogActions-root": {
              padding: 0,
            },
          }}
        >
          <DialogTitle
            sx={{
              color: "text.dark",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "20px",
            }}
          >
            {dialogTitle}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handlePanelClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.fontLight2",
            }}
          >
            <CrossIcon />
          </IconButton>
          <DialogContent>
            <Box sx={{ display: "flex", columnGap: "16px", flexDirection: "column", minHeight: "80px" }}>
              {activeDownload === "dailyMealsReportDownload" ? (
                <Box sx={{ display: "flex", columnGap: "12px", alignItems: "baseline" }}>
                  <Box className="form-section">
                    <Typography component="span" className="form-label">
                      Meal Selection
                    </Typography>
                    <Select
                      value={selectedMeal || null}
                      onChange={(event) => setSelectedMeal(String(event.target.value))}
                      sx={{ marginBottom: 0 }}
                    >
                      <MenuItem selected value={" "}>
                        Choose
                      </MenuItem>
                      {meals?.map((meal) => (
                        <MenuItem key={meal.id} value={meal.id}>
                          {meal.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: "flex", columnGap: "12px", alignItems: "baseline" }}>
                  <Box className="form-section">
                    <Typography component="span" className="form-label">
                      User
                    </Typography>
                    <Select
                      value={selection?.userId || null}
                      onChange={(event) => handleFieldChange("userId", event.target.value)}
                      sx={{ marginBottom: 0 }}
                    >
                      <MenuItem selected value={" "}>
                        Choose
                      </MenuItem>
                      {userList?.map((userItem) => (
                        <MenuItem key={userItem.id} value={userItem.id}>
                          {userItem.fullName}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  <Box className="form-section">
                    <Typography component="span" className="form-label">
                      Dietitian
                    </Typography>
                    <Select
                      value={selection?.dietitianId || null}
                      onChange={(event) => handleFieldChange("dietitianId", event.target.value)}
                      sx={{ marginBottom: 0 }}
                    >
                      <MenuItem selected value={" "}>
                        Choose
                      </MenuItem>
                      {dietitianList?.map((dietitianItem) => (
                        <MenuItem key={dietitianItem.id} value={dietitianItem.id}>
                          {dietitianItem.fullName}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  <Box className="form-section">
                    <Typography component="span" className="form-label">
                      Affiliate
                    </Typography>
                    <Select
                      value={selection?.affiliateId || null}
                      onChange={(event) => handleFieldChange("affiliateId", event.target.value)}
                      sx={{ marginBottom: 0 }}
                    >
                      <MenuItem selected value={" "}>
                        Choose
                      </MenuItem>
                      {affiliateList?.map((affiliateItem) => (
                        <MenuItem key={affiliateItem.id} value={affiliateItem.id}>
                          {affiliateItem.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>
              )}
            </Box>
          </DialogContent>

          <DialogActions>
            <LoadingButton
              fullWidth
              disabled={activeDownload === "dailyMealsReportDownload" && checkEmpty(selectedMeal.trim())}
              loading={
                loading ||
                deliveryPackageReportDownloadProcess ||
                mealBoxStickersDownloadProcess ||
                mealPackageStickersDownloadProcess ||
                dailyMealsReportDownloadProcess
              }
              color="secondary"
              variant="contained"
              sx={{ display: "flex", flexDirection: "column", marginTop: "16px" }}
              onClick={download}
            >
              Download
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Fragment>
    </>
  );
}
